import React from 'react';
import { SlLimitedInput } from 'src/components/BeaconV2Components/SlLimitedInput';
import color from 'src/utils/colors';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SxProps } from '@mui/system';
import { Controller, FieldValues, Path, useFormContext, PathValue } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import { FormControl } from '@mui/material';
// Removed schema-level type to avoid overly broad defaultValue typing

interface StyledSettingProps {
  label?: string;
  textSx?: SxProps;
  required?: boolean;
}

const StyledInput = styled(SlLimitedInput)(() => ({
  width: '100%',
  marginTop: '8px',
  padding: '10px 14px',
  boxSizing: 'border-box',
  '& .MuiInputBase-root': {
    borderRadius: '4px',
    backgroundColor: color.white,
    height: '48px',
    fontSize: '14px'
  },
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: color.grey,
    opacity: '0.5'
  },
  '& .MuiFormLabel-root': {
    color: color.blueGrey
  }
}));

export const InputLabel: React.FC<StyledSettingProps> = ({ textSx, label, required }) => {
  return (
    <Text
      variant="h7"
      sx={{
        ...textSx,
        display: 'flex',
        lineHeight: '20px'
      }}
    >
      {label}
      {required && (
        <Text
          variant="body3"
          sx={{
            lineHeight: '18px',
            marginLeft: '2px'
          }}
        >
          *
        </Text>
      )}
    </Text>
  );
};

type RHFInputProps<T extends FieldValues> = StyledSettingProps & {
  name: Path<T>; // name is a required prop
  characterLimit: number;
  placeholder: string;
  defaultValue?: string; // provide empty string, if no default value is needed
  filterRegex?: RegExp; // Optional regex to filter input characters
};

function AlertStyledInput<T extends FieldValues>(allProps: RHFInputProps<T>) {
  const { control } = useFormContext<T>();
  const {
    name,
    characterLimit,
    placeholder,
    label,
    defaultValue = '',
    required = false,
    textSx = undefined,
    filterRegex,
    ...otherInputProps
  } = allProps;

  return (
    <Controller<T>
      control={control}
      name={name}
      defaultValue={defaultValue as PathValue<T, typeof name>}
      render={({ field, fieldState }) => {
        const originalOnChange = field.onChange;
        const hasError = !!fieldState.error;

        const handleFilteredChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          const rawValue = event.target.value;
          let filteredValue = rawValue;
          if (filterRegex) {
            const disallowedCharsRegex = new RegExp(`[^${filterRegex.source}]`, 'g');
            filteredValue = rawValue.replace(disallowedCharsRegex, '');
          }
          originalOnChange(filteredValue); // Updates RHF state
        };

        return (
          <FormControl>
            {label && <InputLabel label={label} textSx={textSx} required={required} />}
            <StyledInput
              {...field}
              value={field.value} // Controlled by RHF state
              onChange={handleFilteredChange}
              characterLimit={characterLimit}
              placeholder={placeholder}
              error={hasError}
              id={typeof name === 'string' ? name : undefined}
              {...otherInputProps}
            />
            {fieldState.error && (
              <Text
                variant="body3"
                sx={{
                  color: color.red,
                  fontSize: '12px'
                }}
              >
                {fieldState.error.message}
              </Text>
            )}
          </FormControl>
        );
      }}
    />
  );
}

export default AlertStyledInput;
